import React from 'react';
import resolveValuePath from '../../../Helpers/resolveValuePath';
import { useContextParameters } from '../SchemaParameters';
import { useBindingScopes } from './scopes';
import { isSet } from './predicate';

/**
 * The parameter names a declaration asks the page for.
 *
 * `contextParameters` names them, and a declaration whose `parameters` is a list is naming them
 * there instead, which is how a lookup source declares them.
 *
 * @param {Object} declaration - A field descriptor or a lookup source.
 *
 * @returns {Array}
 */
export const contextNames = (declaration) => [
  ...(declaration?.contextParameters ?? []),
  ...(Array.isArray(declaration?.parameters) ? declaration.parameters : []),
];

/** The values a declaration carries itself, where it carries any. */
const declaredValues = (declaration) =>
  declaration?.parameters && !Array.isArray(declaration.parameters)
    ? declaration.parameters
    : {};

/**
 * The keys a declaration depends on, paired with the parameter name each answers to and the value
 * the form currently holds for it.
 */
const dependencies = (declaration, form) =>
  (declaration?.dependsOn ?? []).map(({ field: key, as, optional }) => ({
    key,
    name: as || key,
    optional: Boolean(optional),
    value: resolveValuePath(form, key) ?? null,
  }));

/**
 * What one declaration's lookup is asked with, and whether it may be asked at all.
 *
 * Three places supply parameters, each overriding the last: the values the declaration carries, the
 * runtime values the page publishes, and the current value of a sibling it says it depends on.
 *
 * @param {Object} declaration - A field descriptor or a lookup source.
 * @param {Object} ambient - The runtime values it named, by name.
 * @param {Object} form - The surface's branch of the form store.
 *
 * @returns {Object} `{ parameters, ready, waitingOn }`.
 */
export const gatherParameters = (declaration, ambient, form) => {
  const depends = dependencies(declaration, form);
  const waiting = depends.filter(
    ({ optional, value }) => !optional && !isSet(value)
  );

  return {
    parameters: {
      ...declaredValues(declaration),
      ...ambient,
      ...depends.reduce(
        (picked, { name, value }) =>
          isSet(value) ? { ...picked, [name]: value } : picked,
        {}
      ),
    },
    ready: waiting.length === 0,
    waitingOn: waiting.map(({ key }) => key),
  };
};

/**
 * The parameters a field's lookup is asked with, and whether it may be asked at all.
 *
 * A dependency is required unless it says `optional`, so a lookup whose parent is unset is held back
 * and the field names the parent it is waiting for.
 *
 * Where the field's own value and setter are passed, the value is cleared as a dependency it needed
 * stops being set, so an answer cannot outlive the parent it was chosen under. A value already held
 * when the field first draws is left alone, which is what keeps a layout's `defaultValue` on load.
 *
 * @param {Object} field - The field descriptor.
 * @param {String} basePath - The surface's branch of the form store.
 * @param {Object} owned - The field's own `value` and `onChange`, for the ones holding one value.
 *
 * @returns {Object} `{ parameters, ready, waitingOn }` - `waitingOn` holds the keys of the unset
 *   dependencies, for the field to point at.
 */
export const useFieldParameters = (field, basePath = null, owned = null) => {
  const names = React.useMemo(() => contextNames(field), [field]);
  const ambient = useContextParameters(names);
  const { form } = useBindingScopes({
    basePath,
    reads: (field?.dependsOn ?? []).length > 0,
  });
  const answer = gatherParameters(field, ambient, form);
  const wasReady = React.useRef(answer.ready);

  React.useEffect(() => {
    const lost = wasReady.current && !answer.ready;

    wasReady.current = answer.ready;

    if (lost && owned?.onChange && isSet(owned.value))
      owned.onChange(Array.isArray(owned.value) ? [] : '');
    // The value is read as the readiness changes rather than followed, so a keystroke in the field
    // itself is not a reason to run.
  }, [answer.ready]);

  return answer;
};

export default useFieldParameters;
